import { useCallback, useEffect, useState, type RefObject } from 'react'
import {
  ZoomControls as ZoomControlsBar,
  type ZoomControlsUndoRedo,
} from '@/components/intueri/ZoomControls'
import { useZoom } from '@/hooks/useZoom'
import { ZOOM_MAX_PERCENT_VECTOR, ZOOM_MIN_PERCENT } from '@/constants/zoom'
import type { CanvasHandle } from './KnowledgeGraphCanvas'

interface Props {
  canvasRef: RefObject<CanvasHandle | null>
  /**
   * Optional undo/redo block rendered before the zoom buttons. The Knowledge
   * Graph page has nothing to undo; the Tag mapping drill-down's design shows
   * the pair, so it passes them through.
   */
  undoRedo?: ZoomControlsUndoRedo
  /**
   * Let the canvas's own floor go below the app-wide ZOOM_MIN_PERCENT. The
   * Knowledge Graph's Tree View fits a whole tenant at ~10%; without this the
   * buttons and type-in snap it back up to 20%.
   */
  followCanvasFloor?: boolean
}

/**
 * Knowledge-graph zoom control. Thin adapter over the shared <ZoomControls> +
 * `useZoom` (FS-562): linear ratio model — 100% == the fit-to-screen baseline,
 * the readout is `zoom / fitZoom × 100`, +/- and type-in step a uniform 10% and
 * clamp to the 200% vector cap, and the fit button re-frames the whole graph.
 */
const ZoomControls = ({ canvasRef, undoRedo, followCanvasFloor = false }: Props) => {
  const [zoom, setZoom] = useState(1)
  const [fitZoom, setFitZoom] = useState(1)
  // Cytoscape's hard zoom bounds (static config). The readout is zoom/fitZoom,
  // so these map to percent bounds and keep the +/- buttons from staying
  // enabled past the real floor/ceiling on small-fit graphs (FS-562 review).
  const [minZoom, setMinZoom] = useState(0.3)
  const [maxZoom, setMaxZoom] = useState(2)

  useEffect(() => {
    const handle = canvasRef.current
    if (!handle) return
    setZoom(handle.getZoom())
    setFitZoom(handle.getFitZoom())
    setMinZoom(handle.getMinZoom())
    setMaxZoom(handle.getMaxZoom())
    return handle.subscribe((z) => {
      setZoom(z)
      // The fit baseline can change after refit (e.g. graph data swapped);
      // pull the latest on every tick so the percentage stays calibrated.
      setFitZoom(handle.getFitZoom())
      // The floor can move too (the Knowledge Graph's Tree View zooms out further).
      setMinZoom(handle.getMinZoom())
    })
  }, [canvasRef])

  // Stable so useZoom's useCallback chain isn't invalidated every render.
  const onApply = useCallback(
    (level: number) => canvasRef.current?.setZoom(level),
    [canvasRef],
  )

  const base = fitZoom || 1
  const canvasFloor = Math.ceil((minZoom / base) * 100)
  const { percent, zoomIn, zoomOut, setPercent, canZoomIn, canZoomOut } = useZoom({
    model: 'percent',
    scale: zoom,
    baseline: fitZoom,
    // Calibrate the percent floor/ceiling to cytoscape's reachable scale range
    // ([minZoom, maxZoom]) as well as the universal caps, so canZoomOut/In flip
    // off exactly when the canvas can no longer move (no dead button clicks).
    minPercent: followCanvasFloor ? canvasFloor : Math.max(ZOOM_MIN_PERCENT, canvasFloor),
    maxPercent: Math.min(ZOOM_MAX_PERCENT_VECTOR, Math.floor((maxZoom / base) * 100)),
    onApply,
  })

  return (
    <ZoomControlsBar
      percent={percent}
      onZoomIn={zoomIn}
      onZoomOut={zoomOut}
      onSetPercent={setPercent}
      canZoomIn={canZoomIn}
      canZoomOut={canZoomOut}
      onFit={() => canvasRef.current?.fit()}
      undoRedo={undoRedo}
      size="compact"
      variant="embedded"
    />
  )
}

export default ZoomControls
